import { captureError, captureMessage } from './utils/errorUtils';

import type { CustomFunction } from './types';

// Symbol for storing custom functions in global scope
const CUSTOM_FUNCTIONS_REGISTRY = Symbol.for('cem.customFunctions');

interface CustomFunctionsRegistryWindow extends Window {
  [CUSTOM_FUNCTIONS_REGISTRY]?: Map<string, CustomFunction>;
}

// Initialize or get existing registry
const getOrCreateRegistry = () => {
  const win = window as CustomFunctionsRegistryWindow;
  if (!win[CUSTOM_FUNCTIONS_REGISTRY]) {
    win[CUSTOM_FUNCTIONS_REGISTRY] = new Map();
  }
  return win[CUSTOM_FUNCTIONS_REGISTRY];
};

const customFunctionsRegistry = getOrCreateRegistry();

/**
 * Register a custom function that can be called by name
 * @param name - The unique identifier for the function
 * @param fn - The function to register
 */
export const registerCustomFunction = (
  name: string,
  fn: CustomFunction
): void => {
  if (!name?.trim()) {
    captureMessage('Empty Custom Function name');
    return;
  }

  if (typeof fn !== 'function') {
    captureMessage(`Custom Function "${name}" is not a function`);
    return;
  }

  if (customFunctionsRegistry.get(name)) {
    captureMessage(`Custom Function "${name}" already registered`, {
      level: 'warning',
    });
  }
  customFunctionsRegistry.set(name, fn);
};

/**
 * Unregister a previously registered custom function
 * @param name - The name of the function to unregister
 */
export const unregisterCustomFunction = (name: string): void => {
  customFunctionsRegistry.delete(name);
};

/**
 * Get a custom function by name
 * @param name - The name of the function to get
 * @param nudgeName - The unique name of the nudge invoking the function, reported as metadata so a broken function can be traced back to its nudge
 * @param silent - When true, suppress the "not found" message
 * @returns The function or undefined if not found
 */
export const getCustomFunction = (
  name: string,
  nudgeName: string,
  silent = false
): CustomFunction | undefined => {
  if (!name?.trim()) {
    captureMessage('Empty Custom Function name');
    return;
  }
  const customFunction = customFunctionsRegistry.get(name);
  if (!customFunction) {
    if (!silent) {
      captureMessage(`Custom Function "${name}" not found`, { nudgeName });
    }
    return;
  }

  return (...args) => {
    try {
      return customFunction(...args);
    } catch (error) {
      captureError(`Custom Function "${name}" failed`, error, { nudgeName });
    }
  };
};
